/**
 * Deal Alerts Panel (LPM-1 read surface)
 *
 * Compact list of the tenant's participation-marketplace deal alerts --
 * broadcast fi_treasury Notification rows from the buy-box match fan-out,
 * read via GET /api/v1/deal-alerts. Rendered on the Marketplace page, the
 * treasury persona's working surface. Collapsed entirely when there are no
 * alerts and the fetch is clean (no empty chrome on a page that already has
 * a grid EmptyState).
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Card, Badge, Button, Skeleton } from '@aaimplatform/ui';
import { dealAlertsApi } from '../../api/services/deal-alerts';

const PANEL_LIMIT = 5;

export function DealAlertsPanel() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['deal-alerts', PANEL_LIMIT],
    queryFn: () => dealAlertsApi.list(PANEL_LIMIT),
    staleTime: 60_000,
  });

  if (isLoading) {
    return <Skeleton className="h-20 mb-6" />;
  }
  // A 403 (no pledge:read:inbox) or transient failure collapses the panel;
  // the Marketplace grid is the page's primary surface and stays usable.
  if (error || !data || data.alerts.length === 0) {
    return null;
  }

  return (
    <Card className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-text-secondary" />
        <span className="text-sm font-sans font-semibold">Deal alerts</span>
        <Badge variant="neutral">{data.alerts.length}</Badge>
      </div>
      <ul className="space-y-3">
        {data.alerts.map((alert) => (
          <li key={alert.id} className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-sans font-medium truncate">{alert.title}</div>
              <div className="text-xs font-sans text-text-secondary line-clamp-2">
                {alert.body}
              </div>
              <div className="text-xs font-mono text-text-tertiary mt-1">
                {new Date(alert.createdAt).toLocaleString('en-US')}
              </div>
            </div>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => navigate(`/syndication/offerings/${alert.offeringId}`)}
            >
              View deal
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
